/*
Author: Irrevaldy
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';
import PropTypes from 'prop-types';

import { isVn } from '@global/helpers';

const propTypes = {
  testID: PropTypes.string,
  editable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  isAll: PropTypes.bool,
  searchWithCode: PropTypes.bool,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  placeholder: PropTypes.string,
  useCallbackOptions: PropTypes.array,
};

const defaultProps = {
  testID: 'delivery-address-search',
  editable: true,
  isRequired: false,
  multiple: false,
  hasError: false,
  onChange: () => {},
  loading: false,
  customFilter: {},
  value: '',
  isAll: false,
  label: 'Delivery / Store / Outlet Address',
  labelKey: 'name',
  placeholder: 'Select Delivery / Store / Outlet Address',
  searchWithCode: false,
  useCallbackOptions: [],
};

const limit = 10;

const DeliveryAddressSearch = (props) => {
  const {
    editable = true,
    isRequired = false,
    multiple = false,
    loading: isLoadingProps,
    customFilter = {},
    hasError = false,
    value = '',
    isAll = false,
    label = 'Delivery / Store / Outlet Address',
    labelKey = 'name',
    placeholder = 'Select Delivery / Store / Outlet Address',
    searchWithCode = false,
    useCallbackOptions = [],
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);

  const getCustomers = useApi(MsAccount.getCustomers);
  const getOutlets = useApi(MsAccount.getOutlets);

  const getAddressName = (addr) => {
    return (
      addr?.name ||
      addr?.outlet_name ||
      [addr?.street, addr?.city?.name].filter(Boolean).join(' - ') ||
      ''
    );
  };

  const createDeliveryOption = ({ customer, addr }) => {
    if (!addr || addr?.is_deleted || !addr?._id) {
      return null;
    }

    const customerName = customer?.name || '';
    const addressName = getAddressName(addr);
    const refId = String(addr._id);

    return {
      id: `delivery-${refId}`,
      ref_id: refId,
      address_ref_type: 'delivery_address',
      address_type: 'delivery',
      name: customerName ? `${customerName} - ${addressName}` : addressName,
      address_name: addressName,
      contact_person: addr?.contact_person,
      street: addr?.street,
      street2: addr?.street2,
      postal_code: addr?.postal_code,
      phone: addr?.phone,
      mobile: addr?.mobile,
      email: addr?.email,
      city: addr?.city,
      state: addr?.state,
      country: addr?.country,
      lat_long: addr?.lat_long,
      store_code: addr?.store_code,
      market_type: addr?.market_type,
      customer_id: customer?._id || customer?.id,
      customer_name: customerName,
      is_sub_distributor: Boolean(customer?.is_sub_distributor),
      raw: addr,
    };
  };

  const createStoreOption = ({ customer, addr }) => {
    if (!addr || addr?.is_deleted || !addr?._id) {
      return null;
    }

    const customerName = customer?.name || '';
    const addressName = getAddressName(addr);
    const refId = String(addr._id);

    return {
      id: `store-${refId}`,
      ref_id: refId,
      address_ref_type: 'store_address',
      address_type: 'store',
      name: customerName ? `${customerName} - ${addressName}` : addressName,
      address_name: addressName,
      contact_person: addr?.contact_person,
      street: addr?.street,
      street2: addr?.street2,
      postal_code: addr?.postal_code,
      phone: addr?.phone,
      mobile: addr?.mobile,
      email: addr?.email,
      city: addr?.city,
      state: addr?.state,
      country: addr?.country,
      lat_long: addr?.lat_long,
      store_code: addr?.store_code,
      market_type: addr?.market_type,
      customer_id: customer?._id || customer?.id,
      customer_name: customerName,
      is_sub_distributor: Boolean(customer?.is_sub_distributor),
      raw: addr,
    };
  };

  const toSearchRegex = (val) => {
    try {
      return val ? new RegExp(val, 'i') : null;
    } catch (e) {
      return null;
    }
  };

  const buildNormalCustomerOptions = (customers = [], val, searchWithCode) => {
    const result = [];
    const regex = toSearchRegex(val);

    (customers || []).forEach((customer) => {
      const deliveryAddresses = Array.isArray(customer?.delivery_address)
        ? customer.delivery_address
        : [];
      const storeAddresses = Array.isArray(customer?.store_address)
        ? customer.store_address
        : [];

      const customerMatches =
        !regex ||
        regex.test(customer?.name || '') ||
        regex.test(customer?.name_latin || '') ||
        (searchWithCode && regex.test(customer?.customer_code || ''));

      deliveryAddresses.forEach((addr) => {
        const addressMatches =
          customerMatches ||
          regex.test(addr?.name || '') ||
          regex.test(addr?.street || '') ||
          regex.test(addr?.name_latin || '') ||
          (searchWithCode && regex.test(String(addr?._id || '')));

        if (!addressMatches) {
          return;
        }

        const option = createDeliveryOption({ customer, addr });
        if (option) {
          result.push(option);
        }
      });

      storeAddresses.forEach((addr) => {
        const addressMatches =
          customerMatches ||
          regex.test(addr?.name || '') ||
          regex.test(addr?.street || '') ||
          regex.test(addr?.name_latin || '') ||
          (searchWithCode &&
            (regex.test(String(addr?._id || '')) ||
              regex.test(addr?.store_code || '')));

        if (!addressMatches) {
          return;
        }

        const option = createStoreOption({ customer, addr });
        if (option) {
          result.push(option);
        }
      });
    });

    return result;
  };

  const buildSubDistributorOutletOptions = async (
    scopedCustomerIds = [],
    val,
  ) => {
    const result = [];

    if (!scopedCustomerIds.length) {
      return result;
    }

    try {
      const filter = {
        'customer.id': { $in: scopedCustomerIds },
      };

      if (val) {
        filter.$and = [
          {
            $or: [
              { name: { $regex: val, $options: 'i' } },
              { street: { $regex: val, $options: 'i' } },
              { 'city.name': { $regex: val, $options: 'i' } },
              { 'customer.name': { $regex: val, $options: 'i' } },
            ],
          },
        ];
      }

      const response = await getOutlets.request({
        params: {
          filter,
          skip: 0,
          limit: 50,
          fields: '',
          sort: '-created_at',
          scoped: {},
        },
      });

      const outlets = response?.data?.data || [];

      outlets.forEach((outlet) => {
        if (!outlet?._id || outlet?.is_deleted) {
          return;
        }

        const customerId = String(
          outlet?.customer?._id || outlet?.customer?.id || '',
        );
        const customerName = outlet?.customer?.name || '';

        const outletName =
          outlet?.name ||
          outlet?.outlet_name ||
          [outlet?.street, outlet?.city?.name].filter(Boolean).join(' - ') ||
          '';

        const refId = String(outlet._id);

        result.push({
          id: `outlet-${refId}`,
          ref_id: refId,
          address_ref_type: 'outlet',
          address_type: 'outlet',
          name: customerName ? `${customerName} - ${outletName}` : outletName,
          address_name: outletName,
          contact_person: outlet?.contact_person,
          street: outlet?.street,
          street2: outlet?.street2,
          postal_code: outlet?.postal_code,
          phone: outlet?.phone,
          mobile: outlet?.mobile,
          email: outlet?.email,
          city: outlet?.city,
          state: outlet?.state,
          country: outlet?.country,
          lat_long: outlet?.lat_long,
          store_code: outlet?.store_code,
          market_type: outlet?.market_type,
          customer_id: customerId,
          customer_name: customerName,
          is_sub_distributor: true,
          raw: outlet,
        });
      });
    } catch (error) {
      // noop
    }

    return result;
  };

  const buildAddressOptions = async (
    customers = [],
    val,
    scopedCustomerIds = [],
  ) => {
    const normalCustomers = (customers || []).filter(
      (customer) => !customer?.is_sub_distributor,
    );

    const normalOptions = buildNormalCustomerOptions(
      normalCustomers,
      val,
      searchWithCode,
    );
    const outletOptions = await buildSubDistributorOutletOptions(
      scopedCustomerIds,
      val,
    );

    const merged = [...normalOptions, ...outletOptions];
    const dedup = new Map();

    merged.forEach((item) => {
      dedup.set(String(item.id), item);
    });

    return Array.from(dedup.values());
  };

  const onSearch = async (val, newSkip = 0) => {
    setLoading((prev) => ({
      ...prev,
      ...(newSkip ? { loadMore: true } : { initial: true }),
    }));

    try {
      let filter = {};
      if (customFilter && Object.keys(customFilter).length) {
        filter = { ...customFilter };
      }

      if (val) {
        const orClause = [
          { name: { $regex: val, $options: 'i' } },
          { 'delivery_address.name': { $regex: val, $options: 'i' } },
          { 'delivery_address.street': { $regex: val, $options: 'i' } },
          { 'store_address.name': { $regex: val, $options: 'i' } },
          { 'store_address.street': { $regex: val, $options: 'i' } },
        ];

        if (isVn()) {
          orClause.push({ name_latin: { $regex: val, $options: 'i' } });
          orClause.push({
            'delivery_address.name_latin': { $regex: val, $options: 'i' },
          });
          orClause.push({
            'store_address.name_latin': { $regex: val, $options: 'i' },
          });
        }

        if (searchWithCode) {
          orClause.push({ customer_code: { $regex: val, $options: 'i' } });
          orClause.push({
            'delivery_address._id': { $regex: val, $options: 'i' },
          });
          orClause.push({
            'store_address._id': { $regex: val, $options: 'i' },
          });
          orClause.push({
            'store_address.store_code': { $regex: val, $options: 'i' },
          });
        }

        filter.$or = orClause;
      }

      const response = await getCustomers.request({
        params: {
          filter,
          skip: newSkip,
          limit,
        },
      });

      const rows = response?.data?.data || [];
      const scopedCustomerIds = Array.isArray(customFilter?._id?.$in)
        ? customFilter._id.$in
        : [];
      const result = await buildAddressOptions(rows, val, scopedCustomerIds);

      if (response?.status === 200 && result.length) {
        if (newSkip) {
          setOptions((prev) => {
            const merged = prev.concat(result);
            const dedup = new Map();

            merged.forEach((x) => {
              dedup.set(String(x.id), x);
            });

            return Array.from(dedup.values());
          });
        } else {
          setOptions(isAll ? [{ id: 'all', name: 'All' }, ...result] : result);
        }
      } else if (!newSkip) {
        setOptions(isAll ? [{ id: 'all', name: 'All' }] : []);
      }
    } catch (error) {
      // noop
    } finally {
      setLoading((prev) => ({
        ...prev,
        ...(newSkip ? { loadMore: false } : { initial: false }),
      }));
    }
  };

  return (
    <SelectBox
      {...props}
      label={label}
      options={options}
      value={value}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : placeholder
      }
      trackBy="id"
      labelKey={labelKey}
      isRequired={isRequired}
      multiple={multiple}
      editable={editable && !loading.initial && !isLoadingProps}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={true}
      loadMoreEmpty={skip >= options.length}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val, 0);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + limit;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      useCallbackOptions={useCallbackOptions}
      onChange={(val) => {
        if (isAll) {
          const allValue = { id: 'all', name: 'All' };
          if (multiple) {
            let newVal = Array.isArray(val) ? [...val] : [];

            if (newVal.length <= 0) {
              newVal.push(allValue);
            } else {
              const idx = newVal.findIndex((c) => c.id === 'all');
              if (idx > -1 && newVal.length > 1) {
                const prevHasAll = Array.isArray(value)
                  ? value.findIndex((c) => c.id === 'all') > -1
                  : false;

                if (prevHasAll) {
                  newVal.splice(idx, 1);
                } else {
                  newVal = [allValue];
                }
              }
            }

            props.onChange(newVal);
            return;
          } else if (!val?.id) {
            props.onChange(allValue);
            return;
          }
        }
        props.onChange(val);
      }}
      onReady={() => {
        if (!isLoadingProps) {
          setSkip(0);
          onSearch(undefined, 0);
        }
      }}
    />
  );
};

DeliveryAddressSearch.propTypes = propTypes;
DeliveryAddressSearch.defaultProps = defaultProps;

export default DeliveryAddressSearch;
